import React, { ReactElement, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  styled,
  Text,
  TextTag,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import {
  ConsumptionAndEmissionData,
  DisplayData,
  DisplayItem,
  FuelTypes,
  GiveawayResponse,
  transformToDisplay,
} from '@org-avp/avp-hck-dga-customerui-commons';
import { Market } from '@org-avp/models';
import { exists } from 'i18next';
import { GiveawayContext } from '@/state/state';
import { minimalScreenWidth } from '@/common/breakpoints';

const StyledContainer = styled.section<{ $splitLayout: boolean }>`
  display: ${(props) => (props.$splitLayout ? 'contents' : 'flex')};
  flex-direction: column;
  width: ${(props) => (props.$splitLayout ? 'auto' : '100%')};
  justify-content: space-between;
  padding-top: ${(props) => (props.$splitLayout ? 0 : props.theme.size.static150)};
  gap: ${(props) => props.theme.size.dynamic0100};

  @media screen and ${minimalScreenWidth.l} {
    gap: ${(props) => props.theme.size.dynamic0150};
    flex-direction: row;
  }
`;

const StyledWltp = styled.div`
  grid-area: wltp;
  min-width: 0;
`;

const StyledStatus = styled.div`
  grid-area: date;
  flex: 0 0 auto;
  justify-self: end;
`;

const StyledConsumption = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${(props) => props.theme.size.static150};
`;

const StyledTextSpacer = styled(Text)`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.size.static100};
`;

const partitioned = (displayItems: Array<DisplayItem>): Array<Array<DisplayItem>> => {
  const displayDataRows: Array<Array<DisplayItem>> = [];
  for (let i = 0; i < displayItems.length; i += 2) {
    displayDataRows.push(displayItems.slice(i, i + 2));
  }
  return displayDataRows;
};

export interface ConsumptionProps {
  splitLayout?: boolean;
}

export const Consumption = ({
  splitLayout = false,
}: ConsumptionProps): React.ReactElement | null => {
  const giveawayData: GiveawayResponse = useContext(GiveawayContext);
  const { consumptionAndEmissionData, market } = giveawayData;
  const { t, i18n } = useTranslation();

  const locale = (translationKey: string) => {
    if (!translationKey) {
      return '';
    }
    const fullKey = `body.details.consumption.${translationKey}`;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const translation = t(fullKey);
    return translation === fullKey ? translationKey : translation;
  };

  const getCategoryTranslationKey = (category: string, fuel: string) => {
    if (category === 'consumption') {
      if (fuel === 'ELECTRICAL') {
        return 'type.electrical';
      }
      return 'type.fuel';
    }
    return category;
  };

  const renderPHEV = (consumption: ConsumptionAndEmissionData): ReactElement => {
    const iceConsumption = consumption.fuels[FuelTypes.PETROL];
    const electricConsumption = consumption.fuels[FuelTypes.ELECTRICAL];
    const { consumptionEmptyBattery } = iceConsumption;

    /* empty battery consumption should only render for specific markets (e.g Germany), in markets not needed
    the language key should not exist in the translation files */
    const hasEnergyConsumptionWithoutBattery = exists(
      'body.details.customConsumption.phevConsumptionEmptyBattery',
    );

    /* co2Class should only render for specific markets (e.g Germany), in markets not needed
    the language key should not exist in the translation files */
    const hasCo2ClassCombined = exists('body.details.customConsumption.co2ClassWeighted');

    /* co2Class should only render for specific markets (e.g Germany), in markets not needed
    the language key should not exist in the translation files */
    const hasCo2ClassWithoutBattery = exists('body.details.customConsumption.co2ClassNoBattery');

    return (
      <StyledTextSpacer tag={TextTag.p} appearance={TokenTextAppearance.label100}>
        <Trans
          i18nKey="body.details.customConsumption.phevEnergyConsumption"
          values={{
            phevConsumption: electricConsumption.consumption?.wltp?.subcycles.combined,
            phevConsumptionUnit: electricConsumption.consumption?.wltp?.unit,
            iceConsumption: iceConsumption.consumption?.wltp?.subcycles.combined,
            iceConsumptionUnit: iceConsumption.consumption?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        {hasEnergyConsumptionWithoutBattery && (
          <Trans
            i18nKey="body.details.customConsumption.phevConsumptionEmptyBattery"
            values={{
              phevEmptyBatteryConsumption: consumptionEmptyBattery?.wltp?.subcycles.combined,
              phevEmptyBatteryConsumptionUnit: consumptionEmptyBattery?.wltp?.unit,
            }}
            shouldUnescape={true}
            components={{ strong: <strong /> }}
            parent="span"
          />
        )}
        <Trans
          i18nKey="body.details.customConsumption.phevCo2Emission"
          values={{
            phevEmissionCombined: iceConsumption.emission?.wltp?.subcycles.combined,
            phevEmissionUnit: iceConsumption.emission?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        {hasCo2ClassCombined && (
          <Trans
            i18nKey="body.details.customConsumption.co2ClassWeighted"
            values={{
              co2Class: consumption.co2Class,
            }}
            shouldUnescape={true}
            components={{ strong: <strong /> }}
            parent="span"
          />
        )}
        {hasCo2ClassWithoutBattery && (
          <Trans
            i18nKey="body.details.customConsumption.co2ClassNoBattery"
            values={{
              co2Class: consumption.co2ClassEmptyBattery,
            }}
            shouldUnescape={true}
            components={{ strong: <strong /> }}
            parent="span"
          />
        )}
      </StyledTextSpacer>
    );
  };

  const renderICE = (consumption: ConsumptionAndEmissionData): ReactElement => {
    const primaryFuel = Object.keys(consumption.fuels)[0] as FuelTypes;
    const iceConsumption = consumption.fuels[primaryFuel];

    /* co2Class should only render for specific markets (e.g Germany), in markets not needed
    the language key should not exist in the translation files */
    const hasCo2Class = exists('body.details.customConsumption.co2Class');

    return (
      <StyledTextSpacer tag={TextTag.p} appearance={TokenTextAppearance.label100}>
        <Trans
          i18nKey="body.details.customConsumption.iceEnergyConsumption"
          values={{
            iceConsumption: iceConsumption.consumption?.wltp?.subcycles.combined,
            iceConsumptionUnit: iceConsumption.consumption?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        <Trans
          i18nKey="body.details.customConsumption.iceCo2Emission"
          values={{
            iceEmissionCombined: iceConsumption.emission?.wltp?.subcycles.combined,
            iceEmissionUnit: iceConsumption.emission?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        {hasCo2Class && (
          <Trans
            i18nKey="body.details.customConsumption.co2Class"
            values={{
              co2Class: consumption.co2Class,
            }}
            shouldUnescape={true}
            components={{ strong: <strong /> }}
            parent="span"
          />
        )}
      </StyledTextSpacer>
    );
  };

  const renderBEV = (consumption: ConsumptionAndEmissionData): ReactElement => {
    const electricalConsumption = consumption.fuels[FuelTypes.ELECTRICAL];

    /* co2Class should only render for specific markets (e.g Germany), in markets not needed
    the language key should not exist in the translation files */
    const hasCo2Class = exists('body.details.customConsumption.co2Class');

    return (
      <StyledTextSpacer tag={TextTag.p} appearance={TokenTextAppearance.label100}>
        <Trans
          i18nKey="body.details.customConsumption.bevEnergyConsumption"
          values={{
            bevConsumption: electricalConsumption.consumption?.wltp?.subcycles.combined,
            bevConsumptionUnit: electricalConsumption.consumption?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        <Trans
          i18nKey="body.details.customConsumption.bevCo2Emission"
          values={{
            bevEmissionCombined: electricalConsumption.emission?.wltp?.subcycles.combined,
            bevEmissionUnit: electricalConsumption.emission?.wltp?.unit,
          }}
          shouldUnescape={true}
          components={{ strong: <strong /> }}
          parent="span"
        />
        {hasCo2Class && (
          <Trans
            i18nKey="body.details.customConsumption.co2Class"
            values={{
              co2Class: consumption.co2Class,
            }}
            shouldUnescape={true}
            components={{ strong: <strong /> }}
            parent="span"
          />
        )}
      </StyledTextSpacer>
    );
  };

  const requiresCustomCAELayer = (): boolean => {
    const customMarkets = [Market.DEU];
    return customMarkets.includes(market);
  };

  const showWltpAsterisk = (): boolean => {
    const customMarkets = [Market.GBR];
    return customMarkets.includes(market);
  };

  const renderConsumptionAndEmissionData = (data: ConsumptionAndEmissionData) => {
    const fuels = Object.keys(data.fuels);

    if (fuels.includes(FuelTypes.PETROL) && fuels.includes(FuelTypes.ELECTRICAL)) {
      return renderPHEV(data);
    }

    if (fuels.includes(FuelTypes.ELECTRICAL)) {
      return renderBEV(data);
    }

    if (
      fuels.includes(FuelTypes.PETROL) ||
      fuels.includes(FuelTypes.DIESEL) ||
      fuels.includes(FuelTypes.CNG)
    ) {
      return renderICE(data);
    }

    throw new Error('Could not render consumption and emission data!');
  };

  const displayData: DisplayData = giveawayData.consumptionAndEmissionData
    ? transformToDisplay(giveawayData.consumptionAndEmissionData)
    : { items: [] };

  const getTextElements = (item: DisplayItem) => {
    return (
      item && (
        <>
          <Text
            bold
            appearance={TokenTextAppearance.label100}
            tag={TextTag.span}
            color={TokenTextColor.secondary}
          >
            {`${locale(item.fuel)} ${locale(getCategoryTranslationKey(item.category, item.fuel))} `}
          </Text>
          <Text
            appearance={TokenTextAppearance.label100}
            tag={TextTag.span}
            color={TokenTextColor.secondary}
          >
            {`${locale('combined')}`}
          </Text>
          <Text
            appearance={TokenTextAppearance.label100}
            tag={TextTag.span}
            color={TokenTextColor.secondary}
          >
            {`: ${item.value.toLocaleString()} ${item.unit} ${locale(item.cycle)}`}
            {showWltpAsterisk() && item.category !== 'consumption' && (
              <Text tag={TextTag.span} color={TokenTextColor.secondary}>
                *
              </Text>
            )}
          </Text>
        </>
      )
    );
  };

  if (displayData.items.length === 0) {
    return null;
  }

  return (
    <>
      <StyledContainer $splitLayout={splitLayout}>
        <StyledWltp data-testid="consumption-emission-section">
          {requiresCustomCAELayer() ? (
            renderConsumptionAndEmissionData(consumptionAndEmissionData!)
          ) : (
            <StyledConsumption>
              {partitioned(displayData.items).map((rowItems: DisplayItem[]) => {
                return (
                  <React.Fragment key={`${rowItems[0].fuel}-${rowItems[0].cycle}`}>
                    <Text
                      tag={TextTag.p}
                      appearance={TokenTextAppearance.label100}
                      color={TokenTextColor.secondary}
                    >
                      {getTextElements(rowItems[0])}
                    </Text>
                    <Text
                      tag={TextTag.p}
                      appearance={TokenTextAppearance.label100}
                      color={TokenTextColor.secondary}
                    >
                      {getTextElements(rowItems[1])}
                    </Text>
                  </React.Fragment>
                );
              })}
            </StyledConsumption>
          )}

          {displayData.eec && (
            <StyledTextSpacer
              data-testid="eec-section"
              bold
              color={TokenTextColor.secondary}
              appearance={TokenTextAppearance.label100}
            >
              {locale('EEC')}:
              <Text bold={false} appearance={TokenTextAppearance.label100} tag={TextTag.span}>
                {displayData.eec}
              </Text>
            </StyledTextSpacer>
          )}
        </StyledWltp>

        <StyledStatus>
          <Text
            bold
            appearance={TokenTextAppearance.label100}
            data-testid="creation-date"
            color={TokenTextColor.secondary}
          >
            {locale('valuesFrom')}{' '}
            <Text bold={false} appearance={TokenTextAppearance.label100} tag={TextTag.span}>
              {`${new Date(giveawayData.createdAt).toLocaleDateString(
                i18n.language.replace('_', '-'),
              )}`}
            </Text>
          </Text>
        </StyledStatus>
      </StyledContainer>
    </>
  );
};
